import memoize, { type Serializer } from 'fast-memoize';

/**
 * Memoizes a function based on the reference of its arguments.
 *
 * This is useful for cases where we have large objects (like the project schema) that
 * are expensive to serialize but should remain stable unless modified.
 *
 * This memoization strategy:
 * - Compares every argument, not just the first one (unlike default lodash memoize)
 * - Does not stringify the arguments (unlike default fast-memoize)
 *
 * Notes:
 * - Will not cache calls with no arguments or if all arguments are undefined.
 */
export const memoizeByReference = <Args extends any[], R>(fn: (...args: Args) => R) => {
  let idCounter = 0;
  const cache: WeakMap<object, string> = new WeakMap();

  const serializer: Serializer = (args) => {
    if (args.length === 0) {
      return 'no-args';
    }
    return args
      .map((arg) => {
        if (typeof arg !== 'object' || arg === null) {
          return `primitive:${String(arg)}`;
        }
        const cachedId = cache.get(arg);
        if (cachedId) {
          return cachedId;
        }
        const id = `ref:${++idCounter}`;
        cache.set(arg, id);
        return id;
      })
      .join('|');
  };

  return memoize(fn, { serializer });
};
